import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Checkbox,
  Space,
  Typography,
  Tag,
  message,
  Popconfirm,
  TimePicker,
  Select,
  Row,
  Col,
  Alert
} from 'antd';
import {
  CloudDownloadOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  SettingOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { stationOwnerAPI } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

interface Backup {
  _id: string;
  backupName: string;
  backupType: 'manual' | 'automatic' | 'scheduled';
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  fileSize: number;
  recordCount: Record<string, number>;
  createdAt: string;
  completedAt?: string;
  error?: string;
}

const BackupRestore: React.FC = () => {
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(false);
  const [backupModalVisible, setBackupModalVisible] = useState(false);
  const [restoreModalVisible, setRestoreModalVisible] = useState(false);
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState<Backup | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [backupForm] = Form.useForm();
  const [restoreForm] = Form.useForm();
  const [scheduleForm] = Form.useForm();
  const [schedule, setSchedule] = useState<any>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchBackups = useCallback(async () => {
    try {
      setLoading(true);
      const response = await stationOwnerAPI.getBackups({
        page: pagination.page,
        limit: pagination.limit
      });
      setBackups(response.backups || []);
      setPagination(prev => ({
        ...prev,
        total: response.pagination?.total || 0
      }));
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Yedeklemeler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit]);

  const fetchSchedule = useCallback(async () => {
    try {
      const response = await stationOwnerAPI.getBackupSchedule();
      setSchedule(response);
      if (response.schedule) {
        scheduleForm.setFieldsValue({
          frequency: response.schedule.frequency,
          time: response.schedule.time ? dayjs(response.schedule.time, 'HH:mm') : null,
          dayOfWeek: response.schedule.dayOfWeek,
          dayOfMonth: response.schedule.dayOfMonth
        });
      }
    } catch (error: any) {
      console.error('Zamanlama yüklenemedi:', error);
    }
  }, [scheduleForm]);

  useEffect(() => {
    fetchBackups();
    fetchSchedule();
  }, [fetchBackups, fetchSchedule]);


  const handleCreateBackup = async (values: any) => {
    try {
      setLoading(true);
      await stationOwnerAPI.createBackup({
        backupName: values.backupName,
        dataTypes: values.dataTypes
      });
      message.success('Yedekleme işlemi başlatıldı');
      setBackupModalVisible(false);
      backupForm.resetFields();
      setTimeout(() => {
        fetchBackups();
      }, 2000);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Yedekleme oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBackup = async (backup: Backup) => {
    try {
      const blob = await stationOwnerAPI.downloadBackup(backup._id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${backup.backupName}.json.gz`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('Yedekleme indirildi');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Yedekleme indirilemedi');
    }
  };

  const handleRestore = async (values: any) => {
    if (!selectedBackup) return;

    try {
      setLoading(true);
      await stationOwnerAPI.restoreBackup(selectedBackup._id, {
        restoreTypes: values.restoreTypes,
        deleteExisting: values.deleteExisting
      });
      message.success('Geri yükleme tamamlandı');
      setRestoreModalVisible(false);
      restoreForm.resetFields();
      setSelectedBackup(null);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Geri yükleme yapılamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBackup = async (id: string) => {
    try {
      await stationOwnerAPI.deleteBackup(id);
      message.success('Yedekleme silindi');
      fetchBackups();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Yedekleme silinemedi');
    }
  };

  const handleUpdateSchedule = async (values: any) => {
    try {
      setLoading(true);
      await stationOwnerAPI.updateBackupSchedule({
        frequency: values.frequency,
        time: values.time.format('HH:mm'),
        dayOfWeek: values.dayOfWeek,
        dayOfMonth: values.dayOfMonth
      });
      message.success('Otomatik yedekleme ayarları güncellendi');
      setScheduleModalVisible(false);
      fetchSchedule();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Ayarlar güncellenemedi');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const columns = [
    {
      title: 'Yedekleme Adı',
      dataIndex: 'backupName',
      key: 'backupName'
    },
    {
      title: 'Tip',
      dataIndex: 'backupType',
      key: 'backupType',
      render: (type: string) => {
        const colors: Record<string, string> = {
          manual: 'blue',
          automatic: 'green',
          scheduled: 'orange'
        };
        const labels: Record<string, string> = {
          manual: 'Manuel',
          automatic: 'Otomatik',
          scheduled: 'Zamanlanmış'
        };
        return <Tag color={colors[type]}>{labels[type]}</Tag>;
      }
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: Record<string, string> = {
          pending: 'default',
          in_progress: 'processing',
          completed: 'success',
          failed: 'error'
        };
        const labels: Record<string, string> = {
          pending: 'Bekliyor',
          in_progress: 'Devam Ediyor',
          completed: 'Tamamlandı',
          failed: 'Başarısız'
        };
        return <Tag color={colors[status]}>{labels[status]}</Tag>;
      }
    },
    {
      title: 'Boyut',
      dataIndex: 'fileSize',
      key: 'fileSize',
      render: (size: number) => formatFileSize(size || 0)
    },
    {
      title: 'Kayıt Sayısı',
      dataIndex: 'recordCount',
      key: 'recordCount',
      render: (count: Record<string, number>) => {
        const total = Object.values(count || {}).reduce((a, b) => a + b, 0);
        return total.toLocaleString();
      }
    },
    {
      title: 'Tarih',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm')
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: Backup) => (
        <Space size="small">
          {record.status === 'completed' && (
            <>
              <Button
                type="link"
                icon={<DownloadOutlined />}
                onClick={() => handleDownloadBackup(record)}
                size="small"
              >
                İndir
              </Button>
              <Button
                type="link"
                icon={<CloudUploadOutlined />}
                onClick={() => {
                  setSelectedBackup(record);
                  setRestoreModalVisible(true);
                }}
                size="small"
              >
                Geri Yükle
              </Button>
            </>
          )}
          <Popconfirm
            title="Yedeklemeyi silmek istediğinize emin misiniz?"
            onConfirm={() => handleDeleteBackup(record._id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              Sil
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: isMobile ? '16px' : '24px' }}>
      <Title level={2} style={{ marginBottom: '24px', fontSize: isMobile ? '20px' : '24px' }}>
        <CloudDownloadOutlined /> Yedekleme ve Geri Yükleme
      </Title>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Manuel Yedekleme</Text>
              <Button
                type="primary"
                icon={<CloudDownloadOutlined />}
                onClick={() => setBackupModalVisible(true)}
                block={isMobile}
              >
                Yedekleme Oluştur
              </Button>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Otomatik Yedekleme</Text>
              <Button
                icon={<SettingOutlined />}
                onClick={() => setScheduleModalVisible(true)}
                block={isMobile}
              >
                Zamanlama Ayarla
              </Button>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Durum</Text>
              {schedule?.isActive ? (
                <Tag color="success">Aktif</Tag>
              ) : (
                <Tag color="default">Pasif</Tag>
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      <Card title="Yedekleme Geçmişi">
        <Table
          columns={columns}
          dataSource={backups}
          loading={loading}
          rowKey="_id"
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            onChange: (page) => setPagination(prev => ({ ...prev, page })),
            showSizeChanger: false,
            responsive: true
          }}
          scroll={{ x: true }}
        />
      </Card>

      {/* Yedekleme Oluştur Modal */}
      <Modal
        title="Yeni Yedekleme Oluştur"
        open={backupModalVisible}
        onCancel={() => {
          setBackupModalVisible(false);
          backupForm.resetFields();
        }}
        footer={null}
        width={isMobile ? '90%' : 600}
      >
        <Form
          form={backupForm}
          layout="vertical"
          onFinish={handleCreateBackup}
          initialValues={{
            dataTypes: {
              stations: true,
              campaigns: true,
              fuelPrices: true,
              stocks: true,
              transactions: true,
              customers: true,
              invoices: true,
              employees: true,
              suppliers: true,
              inventory: true,
              settings: true
            }
          }}
        >
          <Form.Item
            name="backupName"
            label="Yedekleme Adı"
            rules={[{ required: true, message: 'Yedekleme adı gereklidir' }]}
          >
            <input
              type="text"
              className="ant-input"
              placeholder="Örn: Yedekleme_2024_01_15"
            />
          </Form.Item>

          <Form.Item name="dataTypes" label="Yedeklenecek Veriler">
            <Checkbox.Group>
              <Row>
                <Col span={12}>
                  <Checkbox value="stations">İstasyonlar</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="campaigns">Kampanyalar</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="fuelPrices">Yakıt Fiyatları</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="stocks">Stoklar</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="transactions">İşlemler</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="customers">Müşteriler</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="invoices">Faturalar</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="employees">Personel</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="suppliers">Tedarikçiler</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="inventory">Envanter</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="settings">Sistem Ayarları</Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Yedekleme Oluştur
              </Button>
              <Button onClick={() => {
                setBackupModalVisible(false);
                backupForm.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Geri Yükleme Modal */}
      <Modal
        title="Yedekten Geri Yükle"
        open={restoreModalVisible}
        onCancel={() => {
          setRestoreModalVisible(false);
          restoreForm.resetFields();
          setSelectedBackup(null);
        }}
        footer={null}
        width={isMobile ? '90%' : 600}
      >
        {selectedBackup && (
          <Alert
            message="Dikkat"
            description="Geri yükleme işlemi mevcut verileri değiştirebilir veya silebilir. Lütfen dikkatli olun."
            type="warning"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        <Form
          form={restoreForm}
          layout="vertical"
          onFinish={handleRestore}
          initialValues={{
            restoreTypes: {
              stations: true,
              campaigns: true,
              fuelPrices: true,
              stocks: true,
              transactions: true,
              customers: true,
              invoices: true,
              employees: true,
              suppliers: true,
              inventory: true,
              settings: true
            },
            deleteExisting: false
          }}
        >
          <Form.Item name="restoreTypes" label="Geri Yüklenecek Veriler">
            <Checkbox.Group>
              <Row>
                <Col span={12}>
                  <Checkbox value="stations">İstasyonlar</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="campaigns">Kampanyalar</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="fuelPrices">Yakıt Fiyatları</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="stocks">Stoklar</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="transactions">İşlemler</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="customers">Müşteriler</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="invoices">Faturalar</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="employees">Personel</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="suppliers">Tedarikçiler</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="inventory">Envanter</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="settings">Sistem Ayarları</Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item
            name="deleteExisting"
            valuePropName="checked"
          >
            <Checkbox>Mevcut verileri sil ve yedekten geri yükle</Checkbox>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading} danger>
                Geri Yükle
              </Button>
              <Button onClick={() => {
                setRestoreModalVisible(false);
                restoreForm.resetFields();
                setSelectedBackup(null);
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Zamanlama Modal */}
      <Modal
        title="Otomatik Yedekleme Zamanlaması"
        open={scheduleModalVisible}
        onCancel={() => {
          setScheduleModalVisible(false);
          scheduleForm.resetFields();
        }}
        footer={null}
        width={isMobile ? '90%' : 600}
      >
        <Form
          form={scheduleForm}
          layout="vertical"
          onFinish={handleUpdateSchedule}
        >
          <Form.Item
            name="frequency"
            label="Sıklık"
            rules={[{ required: true, message: 'Sıklık seçiniz' }]}
          >
            <Select>
              <Option value="daily">Günlük</Option>
              <Option value="weekly">Haftalık</Option>
              <Option value="monthly">Aylık</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="time"
            label="Saat"
            rules={[{ required: true, message: 'Saat seçiniz' }]}
          >
            <TimePicker format="HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.frequency !== currentValues.frequency}
          >
            {({ getFieldValue }) => {
              const frequency = getFieldValue('frequency');
              return frequency === 'weekly' ? (
                <Form.Item
                  name="dayOfWeek"
                  label="Haftanın Günü"
                  rules={[{ required: true, message: 'Gün seçiniz' }]}
                >
                  <Select>
                    <Option value={1}>Pazartesi</Option>
                    <Option value={2}>Salı</Option>
                    <Option value={3}>Çarşamba</Option>
                    <Option value={4}>Perşembe</Option>
                    <Option value={5}>Cuma</Option>
                    <Option value={6}>Cumartesi</Option>
                    <Option value={0}>Pazar</Option>
                  </Select>
                </Form.Item>
              ) : frequency === 'monthly' ? (
                <Form.Item
                  name="dayOfMonth"
                  label="Ayın Günü"
                  rules={[{ required: true, message: 'Gün seçiniz' }]}
                >
                  <Select>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <Option key={day} value={day}>{day}</Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null;
            }}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                Kaydet
              </Button>
              <Button onClick={() => {
                setScheduleModalVisible(false);
                scheduleForm.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BackupRestore;

